import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Award, Headphones, Wallet, ArrowRight, Search, MapPin, Calendar, Users, Star, Sparkles, Plane, Ship, Building2, Heart } from "lucide-react";
import { api, waLink, GOOGLE_REVIEWS_URL } from "../lib/api";
import PackageCard from "../components/PackageCard";
import LeadForm from "../components/LeadForm";
import Seo from "../components/Seo";

const AGENCY_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  name: "Acchajee Travels",
  url: "https://acchajee-explore.preview.emergentagent.com",
  telephone: "+91 72900 46341",
  email: "travels@acchajeemail.com",
  priceRange: "₹₹",
  aggregateRating: { "@type": "AggregateRating", ratingValue: "4.7", reviewCount: "69" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Mile Stone, 1/2, Khandsa Rd, near Sector 10 A Market, opposite Canara Bank Back Side, Pace City I, Sector 37",
    addressLocality: "Gurugram",
    addressRegion: "Haryana",
    postalCode: "122004",
    addressCountry: "IN",
  },
  areaServed: ["India", "UAE", "Singapore", "Thailand", "Indonesia", "Maldives", "Europe"],
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const HERO = "https://images.pexels.com/photos/2549018/pexels-photo-2549018.jpeg?auto=compress&cs=tinysrgb&w=1920";

const PARTNERS = ["Emirates", "Singapore Airlines", "IATA", "Marriott", "Hyatt", "Royal Caribbean", "Etihad", "Taj Hotels", "Cordelia"];

const INSTA_IMAGES = [
  "https://images.unsplash.com/photo-1615920292477-7ffd3935672c?w=600&q=80",
  "https://images.unsplash.com/photo-1623137285532-ec3df3e9abc7?w=600&q=80",
  "https://images.unsplash.com/photo-1615039666131-964929ad0f1e?w=600&q=80",
  "https://images.pexels.com/photos/15831599/pexels-photo-15831599.jpeg?auto=compress&cs=tinysrgb&w=600",
  "https://images.unsplash.com/photo-1623137285582-8a3608b5ff09?w=600&q=80",
  "https://images.pexels.com/photos/13114891/pexels-photo-13114891.jpeg?auto=compress&cs=tinysrgb&w=600",
];

export default function Home() {
  const [dests, setDests] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [search, setSearch] = useState({ destination: "", date: "", travelers: "2" });

  useEffect(() => {
    api.get("/destinations").then(r => setDests(r.data));
    api.get("/packages?featured=true").then(r => setFeatured(r.data.slice(0, 8)));
    api.get("/testimonials").then(r => setTestimonials(r.data));
  }, []);

  return (
    <div className="pt-16 lg:pt-20">
      <Seo path="/" description="Book international & domestic holiday packages, visa assistance, cruises and honeymoons with Acchajee Travels — IATA approved, 50,000+ happy travellers, best price guarantee." schema={AGENCY_SCHEMA} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO} alt="Maldives overwater villa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        <div className="container-p relative z-10 py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <span className="section-label !text-white/80" data-testid="hero-label">Since 2012 · IATA Approved · 50,000+ Happy Travellers</span>
            <h1 className="h1 !text-white mt-4" data-testid="hero-heading">Explore the World with <span className="text-[#FF7D00]">Acchajee Travels</span></h1>
            <p className="text-lg text-white/85 mt-5 max-w-2xl">International & Domestic Holiday Packages at Best Prices — curated by experts, delivered with warmth.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/packages" className="btn-primary" data-testid="hero-explore-btn">Explore Packages <ArrowRight className="w-4 h-4 ml-2" /></Link>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-wa px-7 py-3.5" data-testid="hero-wa-btn">WhatsApp Us</a>
            </div>
          </motion.div>
        </div>

      </section>

      {/* Search widget */}
      <div className="relative z-20 -mt-14">
          <div className="container-p">
            <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgb(0,0,0,0.12)] p-4 md:p-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="hero-search-widget">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50">
                <MapPin className="w-5 h-5 text-[#FF7D00]" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Destination</div>
                  <select value={search.destination} onChange={(e) => setSearch({ ...search, destination: e.target.value })} className="bg-transparent text-sm font-medium w-full outline-none" data-testid="search-destination">
                    <option value="">Anywhere</option>
                    {dests.map(d => <option key={d.slug} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50">
                <Calendar className="w-5 h-5 text-[#FF7D00]" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Departure</div>
                  <input type="date" value={search.date} onChange={(e) => setSearch({ ...search, date: e.target.value })} className="bg-transparent text-sm font-medium w-full outline-none" data-testid="search-date" />
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50">
                <Users className="w-5 h-5 text-[#FF7D00]" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Travellers</div>
                  <input type="number" min="1" value={search.travelers} onChange={(e) => setSearch({ ...search, travelers: e.target.value })} className="bg-transparent text-sm font-medium w-full outline-none" data-testid="search-travelers" />
                </div>
              </div>
              <Link to={`/packages${search.destination ? `?destination=${search.destination}` : ""}`} className="btn-primary justify-center" data-testid="search-submit">
                <Search className="w-4 h-4 mr-2" /> Search Holidays
              </Link>
            </div>
          </div>
        </div>

      {/* Popular Destinations - Bento */}
      <section className="section pt-32">
        <div className="container-p">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="section-label">Popular Destinations</span>
              <h2 className="h2 mt-2">Where would you like to go?</h2>
            </div>
            <Link to="/packages" className="text-sm font-semibold text-[#006CE4] flex items-center gap-1" data-testid="dest-viewall">View all destinations <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dests.slice(0, 8).map((d, i) => (
              <Link key={d.slug} to={`/packages?destination=${d.name}`} className={`group relative rounded-2xl overflow-hidden bg-slate-100 ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`} data-testid={`dest-${d.slug}`}>
                <img src={d.image} alt={d.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-xs uppercase tracking-widest opacity-80">{d.tag}</div>
                  <div className="font-display font-bold text-xl md:text-2xl">{d.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="section bg-slate-50">
        <div className="container-p">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="section-label">Featured Packages</span>
              <h2 className="h2 mt-2">Hand-picked for you</h2>
            </div>
            <Link to="/packages" className="btn-outline text-sm px-5 py-2.5" data-testid="pkg-viewall">Browse All</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p, i) => <PackageCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section">
        <div className="container-p">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-label">Why Choose Us</span>
              <h2 className="h2 mt-2 mb-6">The Acchajee promise — trusted by 50,000+ travellers</h2>
              <p className="muted mb-8 max-w-lg">From your first enquiry to your last day of the trip, our travel experts are with you. Real people, personalised itineraries, and prices you'll love.</p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Shield, title: "IATA Approved", d: "Government registered & fully licensed since 2012." },
                  { icon: Award, title: "Best Price Guarantee", d: "Match any genuine quote — we won't be beaten." },
                  { icon: Headphones, title: "24×7 Support", d: "Emergency helpline before, during & after your trip." },
                  { icon: Wallet, title: "0% EMI Options", d: "Book now, pay over easy monthly installments." },
                ].map((f) => (
                  <div key={f.title} className="flex gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center flex-shrink-0"><f.icon className="w-5 h-5 text-[#FF7D00]" strokeWidth={1.5} /></div>
                    <div>
                      <div className="font-semibold text-[#0A2540]">{f.title}</div>
                      <div className="text-sm muted mt-1">{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "50K+", v: "Happy Travellers" },
                { k: "150+", v: "Destinations" },
                { k: "14+", v: "Years Experience" },
                { k: "4.7★", v: "Google Rating" },
              ].map((s) => (
                <div key={s.v} className="card-glass p-8 text-center">
                  <div className="font-display text-4xl font-bold text-[#0A2540]">{s.k}</div>
                  <div className="text-sm muted mt-1">{s.v}</div>
                </div>
              ))}
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="col-span-2 btn-outline justify-center gap-2" data-testid="google-reviews-btn">
                <Star className="w-4 h-4 fill-[#FF7D00] text-[#FF7D00]" /> Read our 69 Google reviews <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services strip */}
      <section className="section bg-slate-50">
        <div className="container-p">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Plane, label: "Flight Booking", to: "/flights" },
              { icon: Ship, label: "Cruise Holidays", to: "/cruise" },
              { icon: Heart, label: "Honeymoon", to: "/honeymoon" },
              { icon: Building2, label: "Corporate Travel", to: "/corporate" },
            ].map((s) => (
              <Link key={s.label} to={s.to} className="card-glass p-6 flex items-center gap-4 hover:-translate-y-1 transition" data-testid={`service-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center"><s.icon className="w-5 h-5 text-[#006CE4]" strokeWidth={1.5} /></div>
                <div className="font-semibold text-[#0A2540]">{s.label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section">
        <div className="container-p">
          <div className="text-left mb-10">
            <span className="section-label">Testimonials</span>
            <h2 className="h2 mt-2">Loved by families & couples across India</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t) => (
              <div key={t.id} className="card-glass p-6" data-testid={`testimonial-${t.id}`}>
                <div className="flex gap-1 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FF7D00] text-[#FF7D00]" />)}</div>
                <p className="text-[#0A2540] leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-100">
                  {t.image && <img src={t.image} alt="" className="w-10 h-10 rounded-full object-cover" />}
                  <div>
                    <div className="font-semibold text-sm text-[#0A2540]">{t.name}</div>
                    <div className="text-xs muted">{t.trip} · {t.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Gallery */}
      <section className="section bg-slate-50">
        <div className="container-p">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="section-label">Instagram</span>
              <h2 className="h2 mt-2">Follow the journey @acchajeetravels</h2>
            </div>
            <a href="https://www.instagram.com/acchajeetravels/" target="_blank" rel="noopener noreferrer" className="btn-outline text-sm px-5 py-2.5" data-testid="insta-follow-btn">Follow Us</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {INSTA_IMAGES.map((img, i) => (
              <a key={img} href="https://www.instagram.com/acchajeetravels/" target="_blank" rel="noopener noreferrer" className="group relative aspect-square rounded-2xl overflow-hidden" data-testid={`insta-${i}`}>
                <img src={img} alt="Acchajee Travels on Instagram" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-[#0A2540]/0 group-hover:bg-[#0A2540]/30 transition-colors duration-300" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Offers CTA */}
      <section className="section">
        <div className="container-p">
          <div className="relative rounded-3xl overflow-hidden bg-[#0A2540] p-10 md:p-16 text-white">
            <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#FF7D00]/20 blur-3xl" />
            <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-[#006CE4]/20 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF7D00]"><Sparkles className="w-3.5 h-3.5" /> Limited-Time Offer</span>
                <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">Save up to ₹15,000 on international honeymoon packages this season</h2>
                <p className="text-white/70 mt-4 max-w-md">Book by month-end and get complimentary romantic dinner + spa credits at select 5-star properties.</p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <Link to="/honeymoon" className="btn-primary" data-testid="offer-honeymoon-btn">Explore Honeymoon</Link>
                  <a href={waLink("Hi, tell me more about the honeymoon offer.")} target="_blank" rel="noopener noreferrer" className="btn-wa px-6">Ask on WhatsApp</a>
                </div>
              </div>
              <div>
                <div className="bg-white rounded-2xl p-6 text-[#0A2540]"><LeadForm compact title="Talk to a Travel Expert" source="home-cta" /></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners marquee */}
      <section className="py-12 border-y border-slate-100">
        <div className="container-p">
          <div className="text-center text-xs uppercase tracking-[0.3em] muted mb-6">Trusted Partners</div>
          <div className="overflow-hidden no-scrollbar">
            <div className="marquee-track flex gap-16 whitespace-nowrap items-center">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <span key={i} className="text-xl md:text-2xl font-display font-semibold text-slate-400">{p}</span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
